class VisualSearchModal {
  static API_URL = '/apps/wallview/api/visual-search';

  constructor(element) {
    this.element = element;
    this.dialog = element.querySelector('.visual-search-modal__dialog');
    this.input = element.querySelector('[data-visual-search-input]');
    this.dropzone = element.querySelector('[data-visual-search-dropzone]');
    this.preview = element.querySelector('[data-visual-search-preview]');
    this.previewImage = element.querySelector('[data-visual-search-preview-image]');
    this.fileName = element.querySelector('[data-visual-search-file-name]');
    this.status = element.querySelector('[data-visual-search-status]');
    this.results = element.querySelector('[data-visual-search-results]');
    this.grid = element.querySelector('[data-visual-search-grid]');
    this.count = element.querySelector('[data-visual-search-count]');
    this.title = element.querySelector('[data-visual-search-title]');
    this.intro = element.querySelector('[data-visual-search-intro]');
    this.referenceLabel = element.querySelector('[data-visual-search-reference-label]');
    this.productResults = new window.MurallsVisualProductResults({
      grid: this.grid,
      count: this.count,
      source: 'visual_search',
      idPrefix: 'VisualSearch'
    });
    this.lastFocus = null;
    this.previewUrl = '';
    this.searchRequest = 0;
    this.cardCache = new Map();
    this.cardRequests = new Set();
    this.cardQueue = [];
    this.activeCardRequests = 0;
    this.favouriteBank = document.createElement('div');
    this.favouriteBank.hidden = true;
    document.body.appendChild(this.favouriteBank);
    this.bind();
  }

  bind() {
    this.grid.addEventListener('click', event => {
      if (event.target.closest('[data-visual-product-tab]')) this.observeSimilarCards();
    });
    document.querySelectorAll('[data-visual-search-open]').forEach((button) => button.addEventListener('click', () => this.open(button)));
    this.element.querySelectorAll('[data-visual-search-close]').forEach((button) => button.addEventListener('click', () => this.close()));
    this.element.querySelector('[data-visual-search-change]').addEventListener('click', () => this.input.click());
    this.input.addEventListener('change', () => this.handleFile(this.input.files[0]));
    ['dragenter', 'dragover'].forEach((eventName) => this.dropzone.addEventListener(eventName, (event) => {
      event.preventDefault();
      this.dropzone.classList.add('is-dragging');
    }));
    ['dragleave', 'drop'].forEach((eventName) => this.dropzone.addEventListener(eventName, (event) => {
      event.preventDefault();
      this.dropzone.classList.remove('is-dragging');
    }));
    this.dropzone.addEventListener('drop', (event) => this.handleFile(event.dataTransfer.files[0]));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.element.hidden) this.close();
    });
  }

  open(button) {
    this.preserveSimilarFavourites();
    this.favouriteRestoreObserver?.disconnect();
    this.clearSimilarCards();
    this.collectionLinks = {
      'wall-murals': { url: button?.dataset.similarMuralUrl, title: button?.dataset.similarMuralTitle },
      'wallpapers': { url: button?.dataset.similarWallpaperUrl, title: button?.dataset.similarWallpaperTitle }
    };
    this.lastFocus = button || document.activeElement;
    const imageUrl = button?.dataset.visualSearchImage;
    this.element.classList.toggle('is-find-similar', Boolean(imageUrl));
    this.element.hidden = false;
    document.body.classList.add('visual-search-open');
    requestAnimationFrame(() => this.element.classList.add('is-open'));
    this.element.querySelector('.visual-search-modal__close').focus();

    if (imageUrl) {
      this.productResults.setSource('find_similar');
      this.title.textContent = 'Find similar designs';
      this.intro.textContent = 'Explore the closest visual matches to this design.';
      this.referenceLabel.textContent = 'This design';
      this.searchProductImage(imageUrl, button.dataset.visualSearchTitle, button.dataset.visualSearchHandle, button.dataset.visualSearchType);
    } else {
      this.productResults.setSource('visual_search');
      this.title.textContent = 'Find wallpaper from an image';
      this.intro.textContent = 'Upload a reference image and discover visually similar designs.';
      this.referenceLabel.textContent = 'Your reference';
      this.dropzone.hidden = false;
      this.preview.hidden = true;
      this.results.hidden = true;
      this.status.hidden = true;
    }
  }

  close() {
    this.clearSimilarCards();
    this.preserveSimilarFavourites();
    this.restorePageFavourites();
    const recommendations = document.querySelector('product-recommendations');
    if (recommendations) {
      this.favouriteRestoreObserver = new MutationObserver(() => this.restorePageFavourites());
      this.favouriteRestoreObserver.observe(recommendations, {childList:true, subtree:true});
    }
    this.element.classList.remove('is-open');
    document.body.classList.remove('visual-search-open');
    this.searchRequest += 1;
    setTimeout(() => { this.element.hidden = true; }, 180);
    if (this.lastFocus) this.lastFocus.focus();
  }

  async fetchResults(options) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 25000);
    try {
      return await fetch(VisualSearchModal.API_URL, { ...options, signal: controller.signal });
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async handleFile(file) {
    if (!file) return;
    if (!file.type.startsWith('image/')) return this.showStatus('Please choose an image file.', true);
    if (file.size > 5 * 1024 * 1024) return this.showStatus('The image is too large. Please choose one under 5 MB.', true);

    window.MurallsFunnel?.trackUpload('visual_search');
    this.productResults.setSource('visual_search');

    this.title.textContent = 'Find wallpaper from an image';
    this.intro.textContent = 'Upload a reference image and discover visually similar designs.';
    this.referenceLabel.textContent = 'Your reference';
    const requestId = ++this.searchRequest;
    this.results.hidden = true;
    this.preserveSimilarFavourites();
    this.productResults.clear();
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = URL.createObjectURL(file);
    this.previewImage.src = this.previewUrl;
    this.fileName.textContent = file.name;
    this.dropzone.hidden = true;
    this.preview.hidden = false;
    this.showStatus('<span class="visual-search-spinner"></span><strong>Finding similar designs…</strong><small>AI is comparing your image with our collection.</small>');

    try {
      const prepared = await this.prepareImage(file);
      const form = new FormData();
      form.append('image', prepared.blob, 'visual-search.jpg');
      form.append('preferred_type', prepared.width / prepared.height >= 1.15 ? 'Wall Mural' : 'Wallpaper');
      const response = await this.fetchResults({ method: 'POST', body: form });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Search failed');
      if (requestId !== this.searchRequest) return;
      this.renderProducts(data.products || [], data.groups, data.preferredType);
    } catch (error) {
      if (requestId !== this.searchRequest) return;
      console.error('Visual search error:', error);
      this.showStatus('We couldn’t search this image. Please try another one.', true);
    }
  }

  async searchProductImage(imageUrl, title, handle, productType) {
    this.productResults.setSource('find_similar');
    const requestId = ++this.searchRequest;
    const normalizedImageUrl = imageUrl.startsWith('//') ? window.location.protocol + imageUrl : imageUrl;
    this.results.hidden = true;
    this.preserveSimilarFavourites();
    this.productResults.clear();
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = '';
    this.previewImage.src = normalizedImageUrl;
    this.fileName.textContent = title || 'Current product';
    this.dropzone.hidden = true;
    this.preview.hidden = false;
    this.showStatus('<span class="visual-search-spinner"></span><strong>Finding similar designs…</strong><small>Comparing this artwork with our collection.</small>');

    try {
      const response = await this.fetchResults({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_url: normalizedImageUrl, exclude_handle: handle || '', preferred_type: productType || '' })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Search failed');
      if (requestId !== this.searchRequest) return;
      this.renderProducts(data.products || [], data.groups, data.preferredType);
    } catch (error) {
      if (requestId !== this.searchRequest) return;
      console.error('Similar product search error:', error);
      this.showStatus('We couldn’t find similar designs right now. Please try again.', true);
    }
  }

  prepareImage(file) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      const url = URL.createObjectURL(file);
      image.onload = () => {
        const scale = Math.min(1, 1200 / Math.max(image.naturalWidth, image.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        canvas.toBlob((blob) => blob
          ? resolve({ blob, width: canvas.width, height: canvas.height })
          : reject(new Error('image_conversion_failed')), 'image/jpeg', 0.88);
      };
      image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image_load_failed')); };
      image.src = url;
    });
  }

  showStatus(message, error = false) {
    this.status.hidden = false;
    this.status.classList.toggle('is-error', error);
    this.status.innerHTML = message;
  }

  renderProducts(products, groups, preferredType) {
    this.status.hidden = true;
    if (!products.length) return this.showStatus('No similar products were found. Please try another image.', true);
    const rendered = this.productResults.render(products, groups, preferredType);
    if (!rendered) return this.showStatus('No similar products were found. Please try another image.', true);
    this.results.hidden = false;
    if (this.element.classList.contains('is-find-similar')) {
      this.grid.querySelectorAll('[data-visual-product-panel]').forEach(panel => {
        const collection = this.collectionLinks?.[panel.dataset.visualProductPanel];
        if (!collection?.url) return;
        const browse = document.createElement('a');
        browse.className = 'visual-search-browse';
        browse.href = collection.url;
        browse.textContent = `Explore ${collection.title || 'this collection'} ↗`;
        panel.insertBefore(browse, panel.firstChild);
      });
      this.observeSimilarCards();
    }
  }

  clearSimilarCards() {
    this.cardObserver?.disconnect();
    this.cardQueue = [];
    this.cardRequests.forEach(controller => controller.abort());
  }

  observeSimilarCards() {
    this.cardObserver?.disconnect();
    const panel = this.grid.querySelector('[data-visual-product-panel]:not([hidden])');
    if (!panel || !this.element.classList.contains('is-find-similar')) return;
    const enqueue = card => {
      if (card.dataset.similarState) return;
      card.dataset.similarState = 'queued';
      this.cardQueue.push({card, revision: this.searchRequest});
      this.loadSimilarCards();
    };
    if (!('IntersectionObserver' in window)) {
      [...panel.querySelectorAll('[data-similar-handle]')].slice(0, 4).forEach(enqueue);
      return;
    }
    this.cardObserver = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      this.cardObserver?.unobserve(entry.target);
      enqueue(entry.target);
    }), { root: this.dialog, rootMargin: '160px' });
    panel.querySelectorAll('[data-similar-handle]').forEach(card => this.cardObserver.observe(card));
  }

  loadSimilarCards() {
    while (this.activeCardRequests < 2 && this.cardQueue.length) {
      const {card, revision} = this.cardQueue.shift();
      if (!card.isConnected || revision !== this.searchRequest || this.element.hidden) continue;
      this.activeCardRequests++;
      const controller = new AbortController();
      this.cardRequests.add(controller);
      const timeout = setTimeout(() => controller.abort(), 8000);
      const url = new URL(`/products/${encodeURIComponent(card.dataset.similarHandle)}`, location.origin);
      url.searchParams.set('view', 'similar-card');
      const preview = this.element.dataset.previewThemeId;
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const cached = this.cardCache.get(url.href);
      const request = cached ? Promise.resolve(cached) : fetch(url, {signal: controller.signal, credentials: 'same-origin', priority: 'low'})
        .then(response => { if (!response.ok) throw new Error('similar_card_unavailable'); return response.text(); });
      request.then(html => {
        const template = document.createElement('template');
        template.innerHTML = html;
        const productCard = template.content.querySelector('.card-wrapper');
        if (!productCard || !card.isConnected || revision !== this.searchRequest) return;
        this.cardCache.set(url.href, html);
        productCard.querySelectorAll('a[href]').forEach(link => {
          const target = new URL(link.getAttribute('href'), location.origin);
          if (!target.pathname.includes('/products/')) return;
          if (preview) target.searchParams.set('preview_theme_id', preview);
          link.href = window.MurallsFunnel?.attributedUrl(target.href, 'find_similar') || target.href;
          link.dataset.funnelProductLink = '';
          link.dataset.funnelSource = 'find_similar';
          link.dataset.funnelProductHandle = card.dataset.similarHandle;
        });
        window.MurallsConvertCardPrices?.(productCard);
        card.replaceChildren(productCard);
        card.dataset.similarState = 'ready';
        window.MurallsViewportImages?.observe(card);
        this.linkExistingFavourite(card);
      }).catch(() => { if (card.isConnected) card.dataset.similarState = 'fallback'; })
        .finally(() => {
          clearTimeout(timeout);
          this.cardRequests.delete(controller);
          this.activeCardRequests--;
          this.loadSimilarCards();
        });
    }
  }

  // The wishlist app mounts one native control per product. Keep that control
  // (including its app-owned listeners and state) when changing result views.
  preserveSimilarFavourites() {
    this.grid.querySelectorAll('[data-similar-handle]').forEach(card => {
      const control = card.querySelector('.Vtl-WishlistIcon__Wrapper');
      if (!control) return;
      control.dataset.similarFavouriteHandle = card.dataset.similarHandle;
      this.favouriteBank.appendChild(control);
    });
  }

  pageCardFor(handle) {
    return [...document.querySelectorAll('.card-wrapper')].find(card => {
      if (this.element.contains(card)) return false;
      const link = card.querySelector('a[href*="/products/"]');
      return link && new URL(link.href).pathname.endsWith(`/products/${handle}`);
    });
  }

  restorePageFavourites() {
    [...this.favouriteBank.children].forEach(control => {
      const target = this.pageCardFor(control.dataset.similarFavouriteHandle);
      if (target && !target.querySelector('.Vtl-WishlistIcon__Wrapper')) target.appendChild(control);
    });
  }

  linkExistingFavourite(card) {
    const handle = card.dataset.similarHandle;
    const saved = [...this.favouriteBank.children].find(control => control.dataset.similarFavouriteHandle === handle);
    const control = saved || this.pageCardFor(handle)?.querySelector('.Vtl-WishlistIcon__Wrapper');
    if (control) card.querySelector('.card-wrapper')?.appendChild(control);
  }
}

const initializeVisualSearch = () => {
  const element = document.getElementById('VisualSearchModal');
  if (element && !window.murallsVisualSearch) window.murallsVisualSearch = new VisualSearchModal(element);
};

document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-funnel-product-link]');
  if (!link) return;
  window.MurallsFunnel?.trackProductClick(link.dataset.funnelSource, { productHandle: link.dataset.funnelProductHandle });
}, true);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeVisualSearch, { once: true });
} else {
  initializeVisualSearch();
}
